import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

interface CampaignIdea {
  id: string;
  title: string;
  tagLine: string;
  description: string;
  offerDetails: string;
  eventDate: string;
  event: {
    name: string;
    date: string;
    description: string;
  };
  flyer: {
    image: {
      storage_url: string;
      name: string;
      properties?: {
        width?: number;
        height?: number;
      };
    };
  };
  menuItems?: Array<{ items: string }>;
  combo?: Array<{ combo: string }>;
  validFrom?: string;
  validTo?: string;
  onUseIdea?: () => void;
}

interface FourBigIdeasCarouselProps {
  ideas: any[];
  autoplayInterval?: number;
}

const FourBigIdeasCarousel: React.FC<FourBigIdeasCarouselProps> = ({
  ideas,
  autoplayInterval = 5000
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Autoplay for mobile
  useEffect(() => {
    if (isMobile && ideas.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % ideas.length);
      }, autoplayInterval);

      return () => clearInterval(interval);
    }
  }, [isMobile, ideas.length, autoplayInterval]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + ideas.length) % ideas.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % ideas.length);
  };

  // For desktop, show 4 cards at a time
  const visibleCards = isMobile ? [ideas[currentIndex]] : ideas.slice(0, 4);

  return (
    <div className="relative">
      {/* Desktop: 4 Cards Grid */}
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4'} gap-6`}>
        <AnimatePresence mode="wait">
          {visibleCards.map((idea, index) => (
            <motion.div
              key={isMobile ? `${idea.id}-${currentIndex}` : idea.id}
              initial={isMobile ? { opacity: 0, x: 50 } : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              exit={isMobile ? { opacity: 0, x: -50 } : {}}
              transition={{ duration: 0.5, delay: isMobile ? 0 : index * 0.1 }}
              className=" cursor-pointer rounded-xl overflow-hidden bg-[#2a2a2a] border border-gray-700/50"
              style={{
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}
            >
              {/* Card Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={idea.flyer?.image?.storage_url || idea.image}
                  alt={idea.title}
                  className="w-full h-full object-cover object-center"
                />
                {/* Event Name Badge */}
                {idea.event?.name && (
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 bg-[#EE4423]/90 text-white text-xs rounded-full font-medium">
                      {idea.event.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Card Content */}
              <div className="p-4">
                {/* Title */}
                <h3 className="text-white font-semibold text-sm mb-2 line-clamp-2 min-h-[40px]">
                  {idea.title}
                </h3>
                
                {/* Tagline */}
                {idea.tagLine && (
                  <p className="text-[#EE4423] text-xs italic mb-2 line-clamp-1">
                    {idea.tagLine}
                  </p>
                )}
                
                {/* Offer Details */}
                {idea.offerDetails && (
                  <p className="text-gray-300 text-xs mb-2 font-medium">
                    {idea.offerDetails}
                  </p>
                )}
                
                {/* Event Date */}
                {idea.eventDate && (
                  <p className="text-gray-400 text-xs">
                    {new Date(idea.eventDate).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Mobile Navigation Arrows */}
      {isMobile && ideas.length > 1 && (
        <>
          <button
            onClick={handlePrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-[#EE4423] p-2 rounded-full hover:bg-[#d63d20] transition-colors shadow-lg"
          >
            <MdChevronLeft size={24} className="text-white" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-[#EE4423] p-2 rounded-full hover:bg-[#d63d20] transition-colors shadow-lg"
          >
            <MdChevronRight size={24} className="text-white" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-4">
            {ideas.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-[#EE4423] w-6'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default FourBigIdeasCarousel;
